import { useMemo } from 'react';
import {
  ActivityIndicator,
  Pressable,
  Text,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors, radius, shadows, typography } from '@/theme/tokens';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface Props extends Omit<PressableProps, 'children' | 'style'> {
  label: string;
  onPress?: () => void;
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  disabled?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  fullWidth?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  iconLeft,
  iconRight,
  fullWidth = false,
  style,
  ...rest
}: Props) {
  const palette = useMemo(() => paletteFor(variant), [variant]);
  const dims = useMemo(() => dimsFor(size), [size]);
  const isDisabled = disabled || loading;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      onPress={onPress}
      disabled={isDisabled}
      style={({ pressed, hovered }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingHorizontal: dims.px,
          height: dims.h,
          borderRadius: radius['2xl'],
          backgroundColor: palette.bg,
          borderWidth: palette.borderWidth,
          borderColor: palette.border,
          alignSelf: fullWidth ? 'stretch' : 'flex-start',
          opacity: isDisabled ? 0.55 : 1,
          transform: [{ scale: pressed ? 0.985 : 1 }],
          ...(variant === 'primary' && !isDisabled ? shadows.glow : shadows.soft),
          // @ts-ignore web only
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          // @ts-ignore web only
          transitionProperty: 'transform, background-color, box-shadow',
          // @ts-ignore web only
          transitionDuration: '180ms',
          ...(hovered && !isDisabled ? { backgroundColor: palette.bgHover } : null),
        },
        style,
      ]}
      {...rest}
    >
      {loading ? (
        <ActivityIndicator size="small" color={palette.fg} />
      ) : (
        <>
          {iconLeft ? <View>{iconLeft}</View> : null}
          <Text
            style={{
              color: palette.fg,
              fontFamily: typography.body.fontFamily,
              fontSize: dims.fs,
              fontWeight: typography.weights.bold,
              letterSpacing: 0.2,
            }}
            numberOfLines={1}
          >
            {label}
          </Text>
          {iconRight ? <View>{iconRight}</View> : null}
        </>
      )}
    </Pressable>
  );
}

function paletteFor(v: Variant) {
  switch (v) {
    case 'primary':
      return { bg: colors.magenta[700], bgHover: colors.magenta[600], fg: colors.white, border: 'transparent', borderWidth: 0 };
    case 'secondary':
      return { bg: colors.magenta[100], bgHover: colors.magenta[200], fg: colors.magenta[800], border: 'transparent', borderWidth: 0 };
    case 'ghost':
      return { bg: 'transparent', bgHover: colors.magenta[50], fg: colors.magenta[700], border: 'transparent', borderWidth: 0 };
    case 'outline':
      return { bg: colors.white, bgHover: colors.magenta[50], fg: colors.magenta[700], border: colors.magenta[200], borderWidth: 1.5 };
    case 'danger':
      return { bg: colors.danger[500], bgHover: colors.danger[600], fg: colors.white, border: 'transparent', borderWidth: 0 };
    case 'success':
      return { bg: colors.success[500], bgHover: colors.success[600], fg: colors.white, border: 'transparent', borderWidth: 0 };
  }
}

function dimsFor(s: Size) {
  switch (s) {
    case 'sm':
      return { h: 36, px: 14, fs: 13 };
    case 'md':
      return { h: 46, px: 20, fs: 15 };
    case 'lg':
      return { h: 56, px: 28, fs: 17 };
  }
}
